"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Palette, Code, Rocket, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const timelineSteps = [{
  title: "Strategy & Discovery",
  description: "We start by understanding your business goals, target audience, and brand identity to create a roadmap for success.",
  icon: Search,
  badge: "Step 01"
}, {
  title: "Design & Prototyping",
  description: "Our designers create modern, high-fidelity mockups that align with your vision, focusing on user experience and conversion.",
  icon: Palette,
  badge: "Step 02"
}, {
  title: "Development & SEO",
  description: "We build your site using clean, semantic code and optimize it for search engines to ensure fast load times and visibility.",
  icon: Code,
  badge: "Step 03"
}, {
  title: "Launch & Growth",
  description: "After rigorous testing, we launch your new website and provide ongoing support to help your business grow online.",
  icon: Rocket,
  badge: "Step 04"
}];
const TimelineItem = ({
  step,
  index,
  isLast
}: {
  step: (typeof timelineSteps)[0];
  index: number;
  isLast: boolean;
}) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref as React.RefObject<HTMLDivElement>} data-index={index} className={cn("relative flex flex-col md:flex-row items-center w-full mb-16 md:mb-32 transition-all duration-1000 ease-out", isVisible ? "opacity-100" : "opacity-0", isEven ? "md:flex-row-reverse" : "")}>
      
      <div className="hidden md:block w-1/2 px-12" />

      
      <div className="absolute left-4 md:left-1/2 top-0 md:top-12 -translate-x-1/2 z-10 flex flex-col items-center justify-center">
        <div className={cn("w-10 h-10 md:w-14 md:h-14 rounded-full bg-muted dark:bg-[#1a1a1c] border-2 border-primary/50 flex items-center justify-center shadow-[0_0_20px_rgba(61,193,188,0.2)] transition-all duration-700 delay-300", isVisible ? "scale-100 opacity-100" : "scale-0 opacity-0")}>
          <div className="w-full h-full rounded-full flex items-center justify-center relative overflow-hidden group">
             <div className="absolute inset-0 bg-primary/10 group-hover:bg-primary/20 transition-colors" />
             <step.icon className="w-5 h-5 md:w-6 md:h-6 text-primary relative z-10" />
          </div>
        </div>
      </div>

      
      <div className="w-full md:w-1/2 pl-12 md:pl-0 md:px-12">
        <Card className={cn("bg-card/60 dark:bg-[#1a1a1c]/60 backdrop-blur-xl border-white/5 shadow-2xl hover:border-primary/40 transition-all duration-500 rounded-[24px]", "group relative overflow-hidden p-0")}>
          
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 dark:from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
          
          <CardHeader className="pb-4 pt-8 px-8">
            <div className="flex items-center justify-between mb-4">
              <Badge variant="outline" className="rounded-full border-primary/30 text-primary bg-primary/5 dark:bg-primary/10 px-4 py-1.5 text-[10px] font-mono uppercase tracking-widest">
                {step.badge}
              </Badge>
              <div className="p-2 rounded-xl bg-muted/5 dark:bg-primary/10 group-hover:bg-primary/10 transition-colors duration-500">
                <step.icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors duration-500" />
              </div>
            </div>
            <CardTitle className="text-2xl md:text-3xl font-extrabold text-foreground dark:text-white tracking-tighter mb-2">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <CardDescription className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {step.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>;
};
export default function Timeline() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll();
  return <section id="timeline" className="bg-background dark:bg-[#0f0f10] py-32 md:py-48 relative overflow-hidden">
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 dark:bg-primary/10 rounded-full blur-[120px] opacity-50" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-primary/5 dark:bg-primary/10 rounded-full blur-[120px] opacity-30" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        
        <div ref={headerRef as React.RefObject<HTMLDivElement>} className={cn("text-center max-w-4xl mx-auto mb-24 md:mb-32 transition-all duration-1000 ease-out", headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <Badge variant="outline" className="mb-6 rounded-full border-primary/20 text-primary bg-primary/5 px-5 py-2 text-xs font-mono uppercase tracking-[0.2em]">
            The Blueprint
          </Badge>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-8 tracking-tighter leading-[1.1]">
            How We Build Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-[#2a8e8a]">Digital Presence</span>
          </h2>
          <p className="text-lg md:text-xl text-[#bcbcbc] max-w-2xl mx-auto leading-relaxed">
            A battle-tested process designed to take your business from offline to online with zero friction and maximum impact.
          </p>
        </div>

        
        <div className="relative max-w-6xl mx-auto">
          
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-[2px] md:-translate-x-1/2 bg-gradient-to-b from-transparent via-primary/30 to-transparent" />
          
          
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-[2px] md:-translate-x-1/2 bg-gradient-to-b from-primary/0 via-primary to-primary/0 opacity-50 shadow-[0_0_15px_rgba(61,193,188,0.5)]" />

          
          <div className="relative z-10">
            {timelineSteps.map((step, i) => <TimelineItem key={i} step={step} index={i} isLast={i === timelineSteps.length - 1} />)}
          </div>
        </div>

        
        <div className="mt-24 text-center">
          <Link to="/ContactPage">
            <Button size="lg" className="rounded-full px-10 py-8 text-lg font-bold bg-gradient-to-br from-primary to-[#2a8e8a] hover:opacity-90 text-white shadow-[0_0_30px_rgba(61,193,188,0.3)] hover:shadow-[0_0_40px_rgba(61,193,188,0.5)] transition-all duration-500 border-none">Start Your Journey</Button>
          </Link>
        </div>
      </div>
    </section>;
}